import { useMemo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlusSignIcon } from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
  SERVICE_CATEGORY_BADGE,
  SERVICE_CATEGORY_LABEL,
  ServiceCategory,
} from "@/features/permission";
import type { ServiceCategoryType } from "@/features/permission";
import { useSystemRoleList } from "../hooks/useSystemRoles";
import { useSystemRoleStore } from "../stores/systemRoleStore";
import type { CategoryFilter } from "../stores/systemRoleStore";

const FILTERS: { value: CategoryFilter; label: string }[] = [
  { value: "ALL", label: "전체" },
  { value: ServiceCategory.DISPATCH, label: "배차" },
  { value: ServiceCategory.FREIGHT_OWNER, label: "화주" },
  { value: ServiceCategory.PARTNER, label: "파트너" },
  { value: ServiceCategory.ADMIN, label: "관리자" },
];

export function SystemRoleList() {
  const { data, isLoading } = useSystemRoleList();
  const filter = useSystemRoleStore((s) => s.categoryFilter);
  const setFilter = useSystemRoleStore((s) => s.setCategoryFilter);
  const openAdd = useSystemRoleStore((s) => s.openDrawerForAdd);
  const openEdit = useSystemRoleStore((s) => s.openDrawerForEdit);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "ALL") return data;
    return data.filter((r) => r.serviceCategory === filter);
  }, [data, filter]);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2">
        <div>
          <h2 className="text-sm font-bold text-gray-900">
            시스템 베이스라인 역할
          </h2>
          <p className="text-xs text-gray-500">
            모든 배차 업체에 자동 복제·동기화되는 기본 역할
          </p>
        </div>
        <Button
          size="sm"
          onClick={openAdd}
          className="bg-blue-600 px-3 py-1.5 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={12} className="mr-1" />
          역할 추가
        </Button>
      </div>

      <div className="flex shrink-0 gap-1 border-b border-gray-200 bg-gray-50 px-3 py-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
              filter === f.value
                ? "bg-white font-medium text-gray-900 shadow-sm"
                : "text-gray-600 hover:bg-white/60"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            조회 중...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            {filter === "ALL"
              ? "등록된 역할이 없습니다"
              : "해당 카테고리의 역할이 없습니다"}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filtered.map((role) => (
              <li
                key={role.guid}
                role="button"
                tabIndex={0}
                onClick={() => openEdit(role.guid)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openEdit(role.guid);
                  }
                }}
                className="flex cursor-pointer items-start justify-between gap-2 px-3 py-2.5 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {role.label}
                    </span>
                    <CategoryBadge category={role.serviceCategory} />
                    {role.isDefault && (
                      <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                        기본
                      </span>
                    )}
                    {!role.isActive && (
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                        비활성
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="font-mono text-xs text-gray-500">
                      {role.code}
                    </span>
                    {role.description && (
                      <span className="truncate text-xs text-gray-500">
                        · {role.description}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CategoryBadge({ category }: { category: ServiceCategoryType }) {
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-xs ${SERVICE_CATEGORY_BADGE[category]}`}
    >
      {SERVICE_CATEGORY_LABEL[category]}
    </span>
  );
}
